import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Palette, Loader2, X } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { OPENERS, FONTS, NAME_FX, INTERACTIONS, SCENE_TYPES } from "../../lib/inviteDesigns";
import { fileUrl } from "../site/HeroSlides";
import { SceneVoice, useVoices } from "./SceneVoice";

function Scenes({ inv, set, ensureSaved }) {
  const scenes = inv.scenes || [];
  const voices = useVoices();
  const [voiceId, setVoiceId] = useState("");
  const vid = voiceId || voices[0]?.voice_id || "";
  const upd = (i, p) => set({ scenes: scenes.map((s, j) => (j === i ? { ...s, ...p } : s)) });
  return (
    <div data-testid="invite-scenes">
      <div className="flex items-center justify-between mb-1.5"><p className="text-xs font-medium">Story scenes <span className="text-[var(--muted)] font-normal">(plays like a short film before the details)</span></p>
        {scenes.length < 6 && <button onClick={() => set({ scenes: [...scenes, { type: "story", title: "", text: "" }] })} className="text-xs text-[var(--gold)] flex items-center gap-1" data-testid="invite-scene-add"><Plus className="w-3 h-3" /> Scene</button>}</div>
      <div className="space-y-2">
        {scenes.map((s, i) => (
          <div key={i} className="border border-[var(--line)] rounded-xl p-2 space-y-1.5" data-testid={`invite-scene-${i}`}>
            <div className="flex gap-1.5">
              <select value={s.type} onChange={(e) => upd(i, { type: e.target.value })} className="input-luxe !py-1 text-xs !w-auto" data-testid={`invite-scene-type-${i}`}>{SCENE_TYPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
              <input value={s.title} onChange={(e) => upd(i, { title: e.target.value })} maxLength={40} placeholder="Title" className="input-luxe !py-1 text-xs flex-1" data-testid={`invite-scene-title-${i}`} />
              <button onClick={() => set({ scenes: scenes.filter((_, j) => j !== i) })} aria-label="Remove scene" data-testid={`invite-scene-del-${i}`}><Trash2 className="w-3.5 h-3.5 text-[var(--muted)]" /></button>
            </div>
            {s.type !== "photos" && s.type !== "rsvp" && <textarea value={s.text} onChange={(e) => upd(i, { text: e.target.value })} maxLength={220} rows={2} placeholder="What this scene says" className="input-luxe !py-1 text-xs resize-none" data-testid={`invite-scene-text-${i}`} />}
            {s.type !== "rsvp" && <div data-testid={`invite-scene-voice-${i}`}><SceneVoice scene={s} onChange={(voice_path) => upd(i, { voice_path })} ensureSaved={ensureSaved} voiceId={vid} /></div>}
          </div>
        ))}
        {!scenes.length && <p className="text-[11px] text-[var(--muted)]">No scenes: guests go straight to the invite after it opens.</p>}
        {scenes.length > 0 && voices.length > 0 && <label className="flex items-center gap-2 text-[11px] text-[var(--muted)]">AI narrator
          <select value={vid} onChange={(e) => setVoiceId(e.target.value)} className="input-luxe !py-1 text-xs flex-1" data-testid="invite-scene-ai-voice">{voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}</option>)}</select></label>}
      </div>
    </div>
  );
}

function Artwork({ inv, setInv, ensureSaved }) {
  const [prompt, setPrompt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { setPrompt(null); }, [inv.invite_id]);
  const value = prompt ?? inv.art_prompt ?? "";
  async function paint() {
    if (value.trim().length < 4) return toast.error("Describe the artwork first");
    setBusy(true);
    try {
      const id = await ensureSaved();
      if (!id) return;
      const { data } = await api.post(`/weddings/mine/invites/${id}/art`, { prompt: value.trim() });
      setInv((x) => ({ ...x, ...data })); toast.success("Your one-of-a-kind artwork is ready");
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  async function remove() { try { await api.delete(`/weddings/mine/invites/${inv.invite_id}/art`); setInv((x) => ({ ...x, art_path: null })); } catch (e) { toast.error(formatApiError(e)); } }
  return (
    <div data-testid="invite-art">
      <p className="text-xs font-medium mb-1.5">One-of-a-kind AI artwork backdrop</p>
      <div className="flex gap-2 items-start">
        {inv.art_path && <div className="relative w-14 h-20 rounded-lg overflow-hidden shrink-0"><img src={fileUrl(inv.art_path)} alt="" className="w-full h-full object-cover" data-testid="invite-art-thumb" /><button onClick={remove} className="absolute top-0.5 right-0.5 bg-black/60 text-white rounded-full p-0.5" aria-label="Remove artwork" data-testid="invite-art-remove"><X className="w-3 h-3" /></button></div>}
        <textarea value={value} onChange={(e) => setPrompt(e.target.value)} maxLength={400} rows={2} placeholder="e.g. a moonlit Tuscan garden with lanterns and climbing roses" className="input-luxe text-xs resize-none flex-1" data-testid="invite-art-prompt" />
      </div>
      <button onClick={paint} disabled={busy} className="btn-outline-gold text-xs mt-2" data-testid="invite-art-generate">{busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Palette className="w-3 h-3" />} {busy ? "Painting (about 20s)…" : inv.art_path ? "Paint a new one" : "Paint my artwork"}</button>
    </div>
  );
}

export function ExperienceEditor({ inv, set, setInv, design, ensureSaved }) {
  const sel = (k, opts, label, def) => (
    <label className="text-[11px] text-[var(--muted)] space-y-1"><span>{label}</span>
      <select value={inv[k] || def} onChange={(e) => set({ [k]: e.target.value })} className="input-luxe !py-1.5 text-xs" data-testid={`invite-${k}`}>{opts.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select></label>
  );
  return (
    <div className="space-y-4 pt-4 border-t border-[var(--line)]" data-testid="invite-experience">
      <p className="text-[11px] uppercase tracking-[0.2em] text-[var(--gold)]">Guest experience</p>
      <div className="grid grid-cols-2 gap-2">
        {sel("opener", Object.entries(OPENERS).map(([k, o]) => [k, o.name]), "Opening animation", design.opener)}
        {sel("interaction", INTERACTIONS, "How guests open it", "tap")}
        {sel("name_fx", NAME_FX, "Your names", "none")}
        {sel("font", FONTS.map((f) => [f, f]), "Font", design.font)}
      </div>
      <Scenes inv={inv} set={set} ensureSaved={ensureSaved} />
      <Artwork inv={inv} setInv={setInv} ensureSaved={ensureSaved} />
    </div>
  );
}
